import './index.css'
import React, { useEffect, useState } from 'react'
import type { BillingProviderComponentsState } from '@disco/state-management/reducers/billing-provider-components'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import Price from 'components/common/pricing2021/pricing-table/pricing-plan/price'
import Button, { type ButtonAppearance } from 'components/ui/button'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Header from 'components/ui/heading'
import Icon from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'
import useContent from './content'

const showingFractions = (number: number): string | number => {
  if (!Number.isInteger(number)) {
    return String(number.toFixed(2))
  }

  return number
}

type Props = {
  values: Record<string, any>
  onSelectAddOns: (...args: Array<any>) => any
  pricingForm: PricingFormState
  billingProviderComponents: BillingProviderComponentsState
}

/**
 * Component for individual add-on configuration
 * 
 * Renders a single add-on with dropdown selection, pricing display,
 * and action button for adding/updating/removing the add-on.
 */
const Component = ({
  id,
  title,
  options,
  productName,
  tooltip,
  description,
  values,
  onSelectAddOns,
}) => {
  const [localValues, setLocalValues] = useState(values)
  const [value, setValue] = useState(values[id])
  let price = options.find(o => o.value === value)?.price || 0
  let buttonAppearance: ButtonAppearance = 'primary'
  let buttonCaption = 'Add to Plan'

  if (Boolean(value) && Boolean(values[id]) && values[id] !== value) {
    buttonCaption = `Update ${title}`
  }

  if (!Boolean(value) && values[id] !== value) {
    buttonCaption = `Remove ${title}`
    buttonAppearance = 'danger'
  }

  useEffect(() => {
    if (values[id] !== value && localValues[id] !== values[id]) {
      setValue(values[id])
    }

    if (localValues[id] !== values[id]) {
      setLocalValues(values)
    }
  }, [values])
  return (
    <div className="AddOns AddOns--Subscription">
      <div className="AddOns_Header">
        <Header size={600}>{title}</Header>
        <Price
          currencySymbol={`$`}
          price={showingFractions(price)}
          period="/month"
        />
      </div>
      <div className="AddOns_DropdownContainer">
        <div className="AddOns_LabelContainer">
          <span className="AddOns_Label">{productName}</span>
          {tooltip && (
            <div className="AddOns_IconContainer">
              <Tooltip content={tooltip} position="top">
                <Icon
                  type="info-outline"
                  color="#42526E"
                  width={12}
                  height={12}
                  className="AddOns_Icon"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <CustomDropdown
          value={value}
          onChange={value => {
            setValue(value?.value)
          }}
          options={options}
        />
      </div>
      <p className="AddOns_Description">{description}</p>
      <div className="AddOns_Line" />
      <Button
        appearance={buttonAppearance}
        caption={buttonCaption}
        disabled={values[id] === value}
        onClick={() => {
          onSelectAddOns({
            id,
            value: {
              value,
            },
          })
        }}
      />
    </div>
  )
}

/**
 * AddOns component for displaying subscription add-ons
 * 
 * Renders a list of available add-ons that can be added to a subscription plan.
 * Each add-on includes configuration options, pricing, and management controls.
 * 
 * @param props - Component props
 * @param props.values - Current values for all add-ons
 * @param props.onSelectAddOns - Callback for add-on selection changes
 * @param props.billingProviderComponents - Billing provider component configuration
 * @param props.pricingForm - Current pricing form state
 * @returns Array of React nodes representing add-on components
 */
const AddOns = ({
  values,
  onSelectAddOns,
  billingProviderComponents,
  pricingForm,
}: Props): Array<React.ReactNode> => {
  const content = useContent({
    billingProviderComponents,
    pricingForm,
  })
  return content.map<React.ReactNode>(props => (
    // @ts-expect-error
    <Component
      {...props}
      key={props.id}
      values={values}
      onSelectAddOns={onSelectAddOns}
    />
  ))
}

export default AddOns